import { Box } from '@mui/material';
import { Drawer, drawerType } from '../../../../../packages/ui/organisms/drawer';
import {
  Button,
  btnType,
  IconButton,
  Input,
  inputSize,
  inputType,
  Text,
  TextError,
} from '../../../../../packages';
import { useFormik } from 'formik';
import { sx } from './ProblemDrawer.styles';
import React from 'react';

export const ProblemDrawer = ({ isOpen, onClose, problem, onSubmit }) => {
  // Check if this problem has goals (from recommendation or regular problem)
  const hasGoals = problem?.goals && problem.goals.length > 0;

  const { errors, values, handleChange, handleSubmit, touched, setFieldValue } = useFormik({
    initialValues: {
      detail: problem?.detail || problem?.recommendedValue || '',
      goals: hasGoals ? problem.goals : [],
    },
    onSubmit: values => {
      const updatedProblem = {
        ...problem,
        detail: values.detail,
        id: problem?.id || null,
      };

      // If we have goals in the form values, use them
      if (hasGoals) {
        updatedProblem.goals = values.goals;

        updatedProblem.goals.forEach(goal => {
          goal.objectives = goal.objectives.filter(obj => obj.details.trim() !== '');
        });
      }

      onSubmit(updatedProblem);
      onClose();
    },
  });

  const handleAddObjective = goalIndex => {
    const updatedGoals = [...values.goals];

    if (!updatedGoals[goalIndex].objectives) {
      updatedGoals[goalIndex].objectives = [];
    }

    // Check if maximum objectives (3) have been reached for this goal
    if (updatedGoals[goalIndex].objectives.length >= 3) {
      return; // Don't add more if already at maximum
    }

    updatedGoals[goalIndex].objectives.push({
      id: null,
      details: '',
    });

    setFieldValue('goals', updatedGoals);
  };

  const handleRemoveObjective = (goalIndex, objectiveIndex) => {
    const updatedGoals = [...values.goals];
    updatedGoals[goalIndex].objectives = updatedGoals[goalIndex].objectives.filter(
      (_, idx) => idx !== objectiveIndex,
    );

    setFieldValue('goals', updatedGoals);
  };

  // Function to check if a goal has reached max objectives (3)
  const hasMaxObjectives = goalIndex => {
    const goal = values.goals[goalIndex];
    return goal.objectives && goal.objectives.length >= 3;
  };

  const isRecommendation = !!problem.recommendationIds;
  return (
    <Drawer open={isOpen} onClose={onClose} variant={drawerType.NORMAL} sx={sx.drawer}>
      <Box sx={sx.wrapper}>
        <Box sx={sx.header}>
          <Text sx={sx.headerTitle}>
            {isRecommendation ? 'Edit Recommendation' : `${problem.new ? 'Add' : 'Edit'} Problem`}
          </Text>
          <IconButton icon="close" onClick={onClose} sx={sx.backBtn} />
        </Box>
        <Box sx={sx.form}>
          <form onSubmit={handleSubmit}>
            <Box sx={sx.formContent}>
              {/* Problem field - always shown */}
              <Box sx={sx.section}>
                <Input
                  value={values.detail}
                  name="detail"
                  onChange={handleChange}
                  variant={inputType.DEFAULT}
                  size={inputSize.M}
                  fullWidth
                  multiline
                  rows={4}
                  placeholder="Problem details"
                  sx={sx.valuesInput}
                />
                <TextError errorMsg={touched.detail ? errors.detail?.toString() : null} />
              </Box>

              {/* Only show goals & objectives if they exist */}
              {hasGoals &&
                values.goals.map((goal, goalIndex) => (
                  <Box key={goal.id || goalIndex}>
                    {/* Goal Name */}
                    <Box sx={sx.section}>
                      <Text sx={sx.sectionTitle}>Goal {goalIndex + 1}</Text>
                      <Box flexDirection="column" display="flex">
                        <Input
                          value={goal.name}
                          onChange={e => {
                            const updatedGoals = [...values.goals];
                            updatedGoals[goalIndex].name = e.target.value;
                            setFieldValue('goals', updatedGoals);
                          }}
                          variant={inputType.DEFAULT}
                          size={inputSize.M}
                          fullWidth
                          placeholder="Goal name"
                          sx={sx.valuesInput}
                        />
                        <Input
                          value={goal.details}
                          onChange={e => {
                            const updatedGoals = [...values.goals];
                            updatedGoals[goalIndex].details = e.target.value;
                            setFieldValue('goals', updatedGoals);
                          }}
                          variant={inputType.DEFAULT}
                          size={inputSize.M}
                          fullWidth
                          multiline
                          rows={4}
                          placeholder="Goal details"
                          sx={sx.valuesInput}
                        />
                      </Box>
                    </Box>

                    {/* Objectives */}
                    <Box sx={sx.section}>
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Text sx={sx.sectionTitle}>Objectives</Text>
                        {!hasMaxObjectives(goalIndex) && (
                          <IconButton
                            icon="plus"
                            onClick={() => handleAddObjective(goalIndex)}
                            sx={sx.addObjective}
                          >
                            Add New
                          </IconButton>
                        )}
                      </Box>
                      <Box flexDirection="column" display="flex">
                        {goal.objectives &&
                          goal.objectives.map((objective, objectiveIndex) => (
                            <Box key={objective.id || objectiveIndex} sx={sx.objectiveItem}>
                              <Input
                                value={objective.details}
                                onChange={e => {
                                  const updatedGoals = [...values.goals];
                                  updatedGoals[goalIndex].objectives[objectiveIndex].details = e.target.value;
                                  setFieldValue('goals', updatedGoals);
                                }}
                                variant={inputType.DEFAULT}
                                size={inputSize.M}
                                fullWidth
                                placeholder="Objective details"
                                sx={sx.valuesInput}
                              />
                              <IconButton
                                icon="close"
                                onClick={() => handleRemoveObjective(goalIndex, objectiveIndex)}
                                sx={sx.removeObjective}
                              />
                            </Box>
                          ))}
                      </Box>
                    </Box>
                  </Box>
                ))}
            </Box>
            <Box sx={sx.footer}>
              <Button variant={btnType.TEXT} onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit">Save</Button>
            </Box>
          </form>
        </Box>
      </Box>
    </Drawer>
  );
};
